import { DialogLevel, useDialogContext } from '@/components/contexts/ModalContext';
import { useTopLevelAssemblies } from '@/resources/assembly/assemblyHandler';
import { useSites } from '@/resources/organizationSettings/sitesHandler';
import { assemblyTypeTranslations } from '@/resources/rfq/i18n';
import { useDemandsWithSourcingScenarios } from '@/resources/sourcingScenario/sourcingScenarioHandlers';
import { useSourcingTemplates } from '@/resources/sourcingTemplate/sourcingTemplateHandler';
import { useSupplierAndStockLocations } from '@/resources/supplierAndStockLocation/supplierAndStockLocationHandler';
import { t, Trans } from '@lingui/macro';
import { isPresent, transEnum } from '@luminovo/commons';
import {
    colorSystem,
    DestructiveSecondaryIconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FieldNumericControlled,
    FieldSelectControlled,
    FieldTextControlled,
    Flexbox,
    FormItem,
    PrimaryButton,
    SecondaryButton,
    SecondaryIconButton,
    Tag,
    Text,
    Tooltip,
} from '@luminovo/design-system';
import { SourcingTemplateDTO } from '@luminovo/http-client';
import { formatQuantity } from '@luminovo/sourcing-core';
import { Add, Close, EditRounded, LockOutlined } from '@mui/icons-material';
import { Box, styled, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { createDemandFormStateDefaultValues } from './DemandDialog/DemandFormItems';
import { useEditDemandDialog } from './DemandDialog/useEditDemandDialog';
import { createDefaultSourcingScenarioFormValues, SourcingScenarioFormValues } from './types';

const StyledTable = styled(Table)({
    tableLayout: 'fixed',
    '& tr:last-child td': {
        borderBottom: 'none',
    },
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: '12px 16px',
    borderBottom: `1px solid ${colorSystem.neutral[2]}`,
    '&.MuiTableCell-head': {
        backgroundColor: colorSystem.neutral[0],
        height: '32px',
    },
}));

export function GeneralTab() {
    const { control } = useFormContext<SourcingScenarioFormValues>();

    return (
        <Flexbox
            flexDirection="column"
            style={{ width: '100%', alignSelf: 'center', alignItems: 'center', overflow: 'auto' }}
        >
            <Flexbox flexDirection="column" gap={'28px'} width={'800px'} alignSelf="center">
                <FormItem label={t`Sourcing scenario name`} required>
                    <FieldTextControlled
                        control={control}
                        name="general.name"
                        required
                        FieldProps={{
                            style: { width: '100%' },
                            placeholder: t`Enter sourcing scenario name`,
                        }}
                    />
                </FormItem>
                <FormItemSourcingTemplate />
                <FormItemDemands />
            </Flexbox>
        </Flexbox>
    );
}

const useSourcingTemplateChangeDialog = () => {
    const { setDialog, closeDialog } = useDialogContext(DialogLevel.Dynamic);
    const { control, setValue } = useFormContext<SourcingScenarioFormValues>();
    const rfqId = useWatch({ control, name: 'general.rfq' });

    const { data: assemblies } = useTopLevelAssemblies(rfqId);
    const { data: supplierAndStockLocations } = useSupplierAndStockLocations();
    const { data: inventorySites } = useSites();

    return {
        openDialog: (sourcingTemplate: SourcingTemplateDTO) =>
            setDialog(
                <Dialog maxWidth="xs" open={true} onClose={() => closeDialog}>
                    <DialogTitle
                        title={t`Are you sure you want to change the sourcing template?`}
                        onClose={closeDialog}
                    />
                    <DialogContent>
                        <Text>{t`This will reset the sourcing scenario to the default values for the new sourcing template.`}</Text>
                    </DialogContent>
                    <DialogActions>
                        <SecondaryButton autoFocus size="medium" onClick={closeDialog}>
                            <Trans>Cancel</Trans>
                        </SecondaryButton>
                        <PrimaryButton
                            size="medium"
                            onClick={() => {
                                const defaultValues = createDefaultSourcingScenarioFormValues({
                                    rfqId,
                                    sourcingTemplate,
                                    assemblies,
                                    supplierAndStockLocations,
                                    inventorySites,
                                });

                                setValue('solutionPreferences', defaultValues.solutionPreferences);
                                setValue('solutionTagsPreferences', defaultValues.solutionTagsPreferences);
                                setValue('supplierPreferences', defaultValues.supplierPreferences);
                                setValue('inventoryPreferences', defaultValues.inventoryPreferences);
                                setValue('totalCostOfOwnershipRules', defaultValues.totalCostOfOwnershipRules);
                                closeDialog();
                            }}
                        >
                            <Trans>Change sourcing template</Trans>
                        </PrimaryButton>
                    </DialogActions>
                </Dialog>,
            ),
    };
};

function FormItemSourcingTemplate() {
    const { control } = useFormContext<SourcingScenarioFormValues>();
    const { openDialog } = useSourcingTemplateChangeDialog();
    const { data: sourcingTemplates } = useSourcingTemplates();

    return (
        <FormItem
            label={t`Sourcing template`}
            variant="description-inlined"
            description={t`Select the sourcing template for this sourcing scenario.`}
        >
            <FieldSelectControlled
                control={control}
                name="general.sourcingTemplate"
                FieldProps={{
                    options: sourcingTemplates,
                    getOptionLabel: (opt) => opt.name,
                    isOptionEqualToValue: (opt, value) => opt.id === value?.id,
                    getOptionSublabel: (opt) => opt.description,
                    getOptionBadge: (opt) => (opt.is_default ? <Tag label={t`Default`} color={'green'} /> : null),
                    disableClearable: true,
                    placeholder: t`Select sourcing template`,
                    size: 'small',
                    onValueChange: (value) => (isPresent(value) ? openDialog(value) : null),
                }}
            />
        </FormItem>
    );
}

interface ExistingDemandRowProps {
    index: number;
    onRemove: (index: number) => void;
}

function ExistingDemandRow({ index, onRemove }: ExistingDemandRowProps) {
    const { control, trigger } = useFormContext<SourcingScenarioFormValues>();
    const rfqId = useWatch({ control, name: 'general.rfq' });
    const demandId = useWatch({ control, name: `assemblyDemands.${index}.data.id` });
    const { data: existingDemands } = useDemandsWithSourcingScenarios(rfqId);
    const existingDemand = existingDemands?.find((d) => d.id === demandId);

    if (!isPresent(existingDemand) || !isPresent(existingDemand.assembly)) {
        return null;
    }

    return (
        <TableRow>
            <StyledTableCell>
                <Text variant="body">{formatQuantity(existingDemand.quantity, { showPiecesUnit: false })}</Text>
            </StyledTableCell>
            <StyledTableCell>
                <Text variant="body" showEllipsis>
                    {existingDemand.assembly.name}
                </Text>
            </StyledTableCell>
            <StyledTableCell>
                <Tag
                    attention="low"
                    color={'neutral'}
                    label={transEnum(existingDemand.assembly.assembly_type.type, assemblyTypeTranslations)}
                />
            </StyledTableCell>
            <StyledTableCell>
                <Flexbox gap={8} alignItems="center">
                    <Tooltip
                        title={t`This demand cannot be modified as it is already used in another sourcing scenario`}
                    >
                        <LockOutlined fontSize="inherit" style={{ margin: '4px' }} />
                    </Tooltip>
                    <Tooltip title={t`Remove demand`}>
                        <DestructiveSecondaryIconButton
                            size={'small'}
                            onClick={() => {
                                onRemove(index);
                                trigger();
                            }}
                        >
                            <Close fontSize="inherit" />
                        </DestructiveSecondaryIconButton>
                    </Tooltip>
                </Flexbox>
            </StyledTableCell>
        </TableRow>
    );
}

interface NewDemandRowProps {
    index: number;
    onRemove: (index: number) => void;
}

function NewDemandRow({ index, onRemove }: NewDemandRowProps) {
    const { control, setValue, trigger } = useFormContext<SourcingScenarioFormValues>();
    const rfqId = useWatch({ control, name: 'general.rfq' });
    const newDemand = useWatch({ control, name: `assemblyDemands.${index}` });
    const { openDialog: openEditDemandDialog } = useEditDemandDialog();
    const { data: assemblies } = useTopLevelAssemblies(rfqId);

    if (newDemand.kind === 'existing') {
        return null;
    }

    return (
        <TableRow>
            <StyledTableCell>
                <FieldNumericControlled
                    control={control}
                    name={`assemblyDemands.${index}.data.quantity.quantity`}
                    required
                    exclusiveMin={0}
                    max={10_000_000}
                    FieldProps={{
                        size: 'small',
                        style: { width: '100%' },
                    }}
                />
            </StyledTableCell>
            <StyledTableCell>
                <FieldSelectControlled
                    control={control}
                    required
                    name={`assemblyDemands.${index}.data.assembly`}
                    FieldProps={{
                        size: 'small',
                        placeholder: t`Select assembly`,
                        options: assemblies,
                        disableClearable: true,
                        getOptionKey: (opt) => opt.id,
                        getOptionLabel: (opt) => opt.designator,
                    }}
                />
            </StyledTableCell>
            <StyledTableCell>
                {isPresent(newDemand.data.assembly) && (
                    <Tag
                        attention="low"
                        color={'neutral'}
                        label={transEnum(newDemand.data.assembly.type.type, assemblyTypeTranslations)}
                    />
                )}
            </StyledTableCell>
            <StyledTableCell>
                <Flexbox gap={8}>
                    <Tooltip title={t`Edit demand`}>
                        <SecondaryIconButton
                            size={'small'}
                            onClick={() => {
                                openEditDemandDialog({
                                    defaultValues: newDemand.data,
                                    onSubmit: (values) => {
                                        setValue(`assemblyDemands.${index}`, {
                                            kind: 'new',
                                            data: values,
                                        });
                                        trigger();
                                    },
                                });
                            }}
                        >
                            <EditRounded fontSize="inherit" />
                        </SecondaryIconButton>
                    </Tooltip>
                    <Tooltip title={t`Remove demand`}>
                        <DestructiveSecondaryIconButton
                            size={'small'}
                            onClick={() => {
                                onRemove(index);
                                trigger();
                            }}
                        >
                            <Close fontSize="inherit" />
                        </DestructiveSecondaryIconButton>
                    </Tooltip>
                </Flexbox>
            </StyledTableCell>
        </TableRow>
    );
}

function FormItemDemands() {
    const { control } = useFormContext<SourcingScenarioFormValues>();
    const rfqId = useWatch({ control, name: 'general.rfq' });
    const { data: assemblies } = useTopLevelAssemblies(rfqId);
    const { fields: demandsFields, append, remove } = useFieldArray({ control, name: 'assemblyDemands' });

    return (
        <FormItem
            label={t`Demands`}
            description={t`Specify the quantities needed for each assembly. These demands will be aggregated and can be used in the quote request workflow.`}
            variant="description-inlined"
        >
            <Box
                sx={{
                    width: '800px',
                    border: `1px solid ${colorSystem.neutral[2]}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                }}
            >
                <StyledTable>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell width="20%">
                                <Text variant="body-small-semibold">{t`Quantity`}</Text>
                            </StyledTableCell>
                            <StyledTableCell width="40%">
                                <Text variant="body-small-semibold">{t`Assembly`}</Text>
                            </StyledTableCell>
                            <StyledTableCell width="20%">
                                <Text variant="body-small-semibold">{t`Type`}</Text>
                            </StyledTableCell>
                            <StyledTableCell width="20%">
                                <Text variant="body-small-semibold">{t`Actions`}</Text>
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <FormItemDemandsError />
                        {demandsFields.map((field, index) => {
                            if (field.kind === 'existing') {
                                return <ExistingDemandRow key={field.id} index={index} onRemove={remove} />;
                            }

                            if (field.kind === 'new') {
                                return <NewDemandRow key={field.id} index={index} onRemove={remove} />;
                            }

                            return null;
                        })}
                    </TableBody>
                </StyledTable>
            </Box>
            <Flexbox gap={8} flexDirection="row" paddingTop={'12px'}>
                <SecondaryButton
                    size="medium"
                    startIcon={<Add />}
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => {
                        if (assemblies.length > 0) {
                            append({
                                kind: 'new',
                                data: createDemandFormStateDefaultValues(rfqId, { assembly: assemblies[0] }),
                            });
                        }
                    }}
                >
                    <Trans>Add demand</Trans>
                </SecondaryButton>
            </Flexbox>
        </FormItem>
    );
}

function FormItemDemandsError() {
    const context = useFormContext<SourcingScenarioFormValues>();
    return (
        <Controller
            control={context.control}
            name={'assemblyDemands'}
            rules={{
                validate: (value) => {
                    if (value.length === 0) {
                        return t`At least one demand must be selected`;
                    }
                },
            }}
            render={({ fieldState }) => {
                if (isPresent(fieldState.error?.root)) {
                    return (
                        <TableRow>
                            <StyledTableCell colSpan={4}>
                                <Text variant="body" color={colorSystem.red[6]} style={{ whiteSpace: 'nowrap' }}>
                                    <Trans>Please add at least one demand to create a sourcing scenario.</Trans>
                                </Text>
                            </StyledTableCell>
                        </TableRow>
                    );
                }
                return <></>;
            }}
        />
    );
}
